/**
 * Start Dispense "Create Item" for a medicine the pharmacy catalogue could not find.
 *
 *   1. The inventory item master decides whether Create is offered. It is searched
 *      in any material group and any store, active or not. NOT_LISTED on its own
 *      proves nothing: prescribe/search also hides items outside the Drug/Pharmacy
 *      groups and outside the store's allowed item classes, and the backend does
 *      not stop duplicate names. An item found here gets no Create.
 *   2. The EMR medicine master only pre-fills the new item's form. It comes from
 *      search_medicines rows with source 'database', i.e. public.main_medicine,
 *      NOT the inventory item table. Rows with source 'drug_registry' are ignored.
 *
 * Matching is on the whole name, ignoring case and extra spaces. Both searches are
 * contains/fuzzy (search_medicines pads its answer with up to 20 look-alikes), so a
 * non-empty result on its own proves nothing.
 *
 * Import-free on purpose so Jest can load it (axios ships ESM).
 */

export const MEDICINE_ORIGIN = {
  CHECKING: 'CHECKING',
  IN_INVENTORY: 'IN_INVENTORY',
  NOT_IN_INVENTORY: 'NOT_IN_INVENTORY',
  FAILED: 'FAILED',
};

const MEDICINE_MASTER_SOURCE = 'database';

/**
 * The material groups the pharmacy catalogue searches. This mirrors
 * dispensing-billing prescribe/search (material_group_names=['Drug','Pharmacy'],
 * compared on lower(group_name)). Start Dispense never finds an item filed under
 * any other group.
 */
const PHARMACY_CATALOGUE_GROUPS = new Set(['drug', 'pharmacy']);

function asText(value) {
  return String(value ?? '').trim();
}

export function medicineNameKey(name) {
  return asText(name).replace(/\s+/g, ' ').toLowerCase();
}

function listOf(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

/** The inventory item already carrying this exact name, or null. */
export function findExactInventoryItem(items, name) {
  const key = medicineNameKey(name);
  if (!key) return null;
  return listOf(items).find((item) => (
    medicineNameKey(item?.item_name) === key || medicineNameKey(item?.brand_name) === key
  )) || null;
}

/**
 * The main_medicine row for this exact name among search_medicines rows, or null.
 * A term match beats a brand-name match: the term is the full product name.
 */
export function findMedicineMasterRow(rows, name) {
  const key = medicineNameKey(name);
  if (!key) return null;
  const masterRows = listOf(rows).filter((row) => row?.source === MEDICINE_MASTER_SOURCE);
  return masterRows.find((row) => medicineNameKey(row.term) === key)
    || masterRows.find((row) => medicineNameKey(row.branded_name) === key)
    || null;
}

/**
 * location.state for the item form. item_name is always the prescribed string,
 * because Start Dispense finds the item by searching for exactly that. The other
 * fields come from main_medicine; with no row, the generic falls back to the
 * prescription's own generic.
 */
export function buildNewMedicineDraft(line, masterRow) {
  return {
    item_name: asText(line?.medicine),
    generic_name: asText(masterRow?.generic_name) || asText(line?.generic_name),
    brand_name: asText(masterRow?.branded_name),
    manufacturer_name: asText(masterRow?.manufacturer_name),
  };
}

export function isPharmacyCatalogueGroup(group) {
  const name = asText(group?.group_name ?? group?.material_group_name).toLowerCase();
  return PHARMACY_CATALOGUE_GROUPS.has(name);
}
